import clsx from "clsx";
import type React from "react";
import "./PortalPage.css";
import Typography from "src/components/common/Typography/Typography";
import { PortalPageHeaderProvider, PortalPageTitle } from "./PortalPageHeaderContext";

export interface PortalPageProps extends React.HTMLAttributes<HTMLElement> {
  children: React.ReactNode;
}

export interface PortalPageHeaderProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * `PortalPage.Heading` and `PortalPage.Actions` share the title row; anything
   * else (summary stats, tabs) renders full width below it.
   */
  children: React.ReactNode;
  /** Keeps the title row and actions pinned under the topbar while scrolling. */
  sticky?: boolean;
}

export interface PortalPageSlotProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export interface PortalPageDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {
  children: React.ReactNode;
}

export interface PortalPageContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export interface PortalPageToolbarProps extends React.HTMLAttributes<HTMLElement> {
  children: React.ReactNode;
}

const PortalPageHeader = (props: PortalPageHeaderProps) => {
  const { children, sticky = false, className, ...otherProps } = props;

  return (
    <header
      className={clsx(
        "calmlens-portal-header",
        sticky && "calmlens-portal-sticky calmlens-portal-header-sticky",
        className,
      )}
      {...otherProps}
    >
      <PortalPageHeaderProvider sticky={sticky}>{children}</PortalPageHeaderProvider>
    </header>
  );
};

/** Title column of the header row: `Title`, then `Description`, `Meta` and `Docs`. */
const PortalPageHeading = (props: PortalPageSlotProps) => {
  const { children, className, ...otherProps } = props;
  return (
    <div className={clsx("calmlens-portal-header-text", className)} {...otherProps}>
      {children}
    </div>
  );
};

/** One line of supporting copy under the title. */
const PortalPageDescription = (props: PortalPageDescriptionProps) => {
  const { children, className, ...otherProps } = props;
  return (
    <Typography
      as="p"
      variant="bodySmall"
      tone="muted"
      className={clsx("max-w-3xl", className)}
      {...otherProps}
    >
      {children}
    </Typography>
  );
};

/** Row of metadata, such as status chips, under the title. */
const PortalPageMeta = (props: PortalPageSlotProps) => {
  const { children, className, ...otherProps } = props;
  return (
    <div className={clsx("flex min-w-0 flex-wrap items-center gap-2", className)} {...otherProps}>
      {children}
    </div>
  );
};

/** Documentation links under the description. */
const PortalPageDocs = (props: PortalPageSlotProps) => {
  const { children, className, ...otherProps } = props;
  return (
    <div className={clsx("calmlens-portal-header-docs", className)} {...otherProps}>
      {children}
    </div>
  );
};

/** Page-level actions, aligned to the end of the title row. */
const PortalPageActions = (props: PortalPageSlotProps) => {
  const { children, className, ...otherProps } = props;
  return (
    <div className={clsx("calmlens-portal-header-actions", className)} {...otherProps}>
      {children}
    </div>
  );
};

const PortalPageContent = (props: PortalPageContentProps) => {
  const { children, className, ...otherProps } = props;

  return (
    <div className={clsx("flex min-w-0 flex-col gap-8", className)} {...otherProps}>
      {children}
    </div>
  );
};

/** Sticky control bar for page-wide filters and view options. */
const PortalPageToolbar = (props: PortalPageToolbarProps) => {
  const { children, className, ...otherProps } = props;
  return (
    <section
      className={clsx(
        "calmlens-portal-sticky calmlens-portal-toolbar flex flex-col gap-4 py-3",
        className,
      )}
      {...otherProps}
    >
      {children}
    </section>
  );
};

/**
 * Action bar pinned to the bottom of the viewport, such as a form's save bar.
 * Render it last in the page; it sits flush with the page's bottom edge.
 */
const PortalPageFooter = (props: PortalPageSlotProps) => {
  const { children, className, ...otherProps } = props;
  return (
    <div className={clsx("calmlens-portal-footer", className)} {...otherProps}>
      {children}
    </div>
  );
};

const PortalPage = (props: PortalPageProps) => {
  const { children, className, ...otherProps } = props;

  return (
    <main
      className={clsx(
        "calmlens-portal-page mx-auto flex min-w-0 w-full max-w-[90rem] flex-col gap-8",
        className,
      )}
      {...otherProps}
    >
      {children}
    </main>
  );
};

PortalPage.Header = PortalPageHeader;
PortalPage.Heading = PortalPageHeading;
PortalPage.Title = PortalPageTitle;
PortalPage.Description = PortalPageDescription;
PortalPage.Meta = PortalPageMeta;
PortalPage.Docs = PortalPageDocs;
PortalPage.Actions = PortalPageActions;
PortalPage.Toolbar = PortalPageToolbar;
PortalPage.Content = PortalPageContent;
PortalPage.Footer = PortalPageFooter;

export default PortalPage;
